export type KakaoSdk = {
  Auth: {
    authorize: (options: {
      nonce: string;
      redirectUri: string;
      scope: string;
      state: string;
      throughTalk?: boolean;
    }) => void;
  };
  init: (key: string) => void;
  isInitialized: () => boolean;
};

type WindowWithKakao = Window & {
  Kakao?: KakaoSdk;
};

type LoadKakaoSdkParams = {
  key: string;
  windowLike?: WindowWithKakao;
  injectScript?: () => Promise<void>;
};

const KAKAO_SDK_SRC = "https://t1.kakaocdn.net/kakao_js_sdk/2.7.6/kakao.min.js";

function defaultInjectScript(windowLike: WindowWithKakao) {
  return async () => {
    await new Promise<void>((resolve, reject) => {
      const script = windowLike.document.createElement("script");
      script.src = KAKAO_SDK_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Kakao JavaScript SDK."));
      windowLike.document.head.appendChild(script);
    });
  };
}

export async function loadKakaoSdk({
  key,
  windowLike = window as WindowWithKakao,
  injectScript = defaultInjectScript(windowLike),
}: LoadKakaoSdkParams) {
  if (!key.trim()) {
    throw new Error("Missing Kakao JavaScript key.");
  }

  if (!windowLike.Kakao) {
    await injectScript();
  }

  const sdk = windowLike.Kakao;

  if (!sdk) {
    throw new Error("Kakao JavaScript SDK did not initialize.");
  }

  if (!sdk.isInitialized()) {
    sdk.init(key);
  }

  return sdk;
}
